import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../LanguageContext';
import { useAuth } from '../AuthContext';
import { can } from '../permissions';
import { fetchCampaigns, saveCampaign, deleteCampaign, duplicateCampaign, publishCampaign, fetchLiveCampaigns, setLiveCampaignStatus, updateLiveCampaign, fetchLiveCampaignDetail, uploadCampaignImage, checkTiktokCreativeAccess, fetchLeadForms, setLiveAdgroupStatus, updateLiveAdgroup,
  fetchSnapLeadForms, connectSnapLeadForm, disconnectSnapLeadForm, setSnapLeadFormOwner, brokersList,
  setLiveAdStatus, updateLiveAd, campaignVideoBlobUrl, fetchTargeting } from '../api';

// Ad Campaign Composer — gradient hero + three tabs (running / drafts / lead-form wiring)
// and a progress-bar wizard. The visual layer lives in index.css as cmpx-* classes so the
// look is consistent and animatable without a UI framework. Publishing always creates the
// campaign PAUSED on the platform — nothing spends until a human switches it on there.
const OBJECTIVES = ['traffic', 'reach', 'engagement', 'video_views', 'leads', 'conversions'];
const OBJ_LABEL = {
  traffic: ['Traffic', 'زيارات الموقع'], reach: ['Reach', 'الوصول'],
  engagement: ['Engagement', 'التفاعل'], video_views: ['Video views', 'مشاهدات الفيديو'],
  leads: ['Leads', 'العملاء المحتملون'], conversions: ['Conversions', 'التحويلات'],
};
// Objective cards: icon + one plain-language line on what the campaign will do —
// a bare <select> assumed the user already knew the platforms' jargon.
const OBJ_META = {
  traffic: ['🔗', ['زيارات لموقعك الإلكتروني', 'Visits to your website']],
  reach: ['📣', ['الوصول لأكبر عدد من الأشخاص', 'Reach as many people as possible']],
  engagement: ['💬', ['إعجابات وتعليقات ومتابعة', 'Likes, comments and follows']],
  video_views: ['▶', ['مشاهدات لفيديو إعلانك', 'Get your video watched']],
  leads: ['👤', ['العميل يترك اسمه ورقمه في نموذج داخل المنصة', 'The customer leaves name & phone in an in-app form']],
  conversions: ['🛒', ['مبيعات أو تسجيلات على موقعك', 'Sales or sign-ups on your website']],
};
const PLATFORMS = [
  { key: 'tiktok', label: ['TikTok', 'تيك توك'], live: true },
  { key: 'snapchat', label: ['Snapchat', 'سناب شات'], live: true },
  { key: 'google', label: ['Google Ads', 'جوجل'], live: true },
  { key: 'meta', label: ['Meta (FB/IG)', 'ميتا'], live: false },
];
const LIVE_PLATFORMS = [
  { key: 'tiktok', label: ['TikTok', 'تيك توك'] },
  { key: 'snapchat', label: ['Snapchat', 'سناب شات'] },
  { key: 'google', label: ['Google Ads', 'جوجل'] },
];
const PLATFORM_COLOR = { tiktok: '#ee1d52', snapchat: '#f7b500', google: '#4285f4', meta: '#0866ff' };
const PLATFORM_GLYPH = { tiktok: 'TT', snapchat: 'SC', google: 'GA', meta: 'f' };
// Per-platform (re)authorization endpoints — server-side OAuth flows. The redirect URI
// each derives from the request host, so these work per-tenant (yn.sa, avia.com.sa, …).
const AUTH_PATHS = { tiktok: '/auth/tiktok', snapchat: '/auth/snapchat', google: '/auth/google' };
const CTAS = ['LEARN_MORE', 'SHOP_NOW', 'SIGN_UP', 'CONTACT_US', 'BOOK_NOW', 'DOWNLOAD'];
// Platforms whose lead-generation forms we can list + attach at publish.
const LEAD_FORM_PLATFORMS = ['tiktok', 'snapchat'];
// The fields a lead form can ask the customer for — shown so the user sees what the
// form collects before publishing. The form itself is built in the platform.
const LEAD_FIELD_LABEL = {
  FIRST_NAME: ['الاسم الأول', 'First name'], LAST_NAME: ['اسم العائلة', 'Last name'],
  NAME: ['الاسم', 'Name'], FULL_NAME: ['الاسم الكامل', 'Full name'], EMAIL: ['البريد الإلكتروني', 'Email'],
  PHONE_NUMBER: ['رقم الجوال', 'Phone'], ADDRESS: ['العنوان', 'Address'],
  POSTAL_CODE: ['الرمز البريدي', 'Postal code'], BIRTHDAY_DATE: ['تاريخ الميلاد', 'Birthday'],
  JOB_TITLE: ['المسمى الوظيفي', 'Job title'], COMPANY_NAME: ['الشركة', 'Company'],
  CUSTOM: ['سؤال مخصص', 'Custom question'],
};
const STEPS = [['الأساسيات', 'Basics'], ['الميزانية والجمهور', 'Budget & audience'], ['الإعلان', 'Creative'], ['المراجعة والنشر', 'Review & publish']];

const emptyForm = () => ({
  name: '', objective: 'traffic', platforms: ['tiktok'], budget: 100, budgetType: 'daily',
  startDate: '', endDate: '',
  audience: { country: 'SA', ageMin: '18', ageMax: '45', gender: 'all', interests: [], cities: [], snapRegions: [] },
  creative: { headline: '', body: '', cta: 'LEARN_MORE', landingUrl: '', imageUrl: '', leadForms: {} },
});

function CampaignComposer() {
  const { lang } = useLanguage();
  const { user } = useAuth();
  const ar = lang === 'ar';
  const L = (a, e) => (ar ? a : e);
  const canPublish = can(user, 'campaigns.publish');
  const canEdit = can(user, 'campaigns.create');

  const [tab, setTab] = useState('live'); // live | drafts | forms
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [wizard, setWizard] = useState(null); // null | { draft: null | existing }
  const [publishingId, setPublishingId] = useState(null);
  const [msg, setMsg] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true); setError(false);
    const res = await fetchCampaigns();
    if (res.error) setError(true); else setCampaigns(res.campaigns || []);
    setLoading(false);
  }, []);
  useEffect(() => { loadData(); }, [loadData]);

  const handlePublish = async (c) => {
    if (!window.confirm(L('تُنشأ الحملة على المنصات المختارة بحالة "متوقفة" — لن يُصرف شيء تلقائيًا. متابعة؟', 'This creates the campaign on the selected platforms PAUSED — nothing spends automatically. Continue?'))) return;
    setPublishingId(c.id); setMsg(null);
    const res = await publishCampaign(c.id);
    setPublishingId(null);
    if (res.error) { setMsg({ type: 'error', text: res.error }); return; }
    await loadData();
  };

  const handleDelete = async (c) => {
    if (!window.confirm(L('حذف هذه المسودة؟', 'Delete this draft?'))) return;
    await deleteCampaign(c.id); await loadData();
  };

  const handleDuplicate = async (c) => {
    setPublishingId(c.id);
    const res = await duplicateCampaign(c.id);
    setPublishingId(null);
    if (res.error) { setMsg({ type: 'error', text: res.error }); return; }
    await loadData();
    setMsg({ type: 'ok', text: L('تم إنشاء نسخة من الحملة', 'Campaign duplicated') });
  };

  if (wizard) {
    return <Wizard ar={ar} canPublish={canPublish} draft={wizard.draft}
      onClose={(published) => { setWizard(null); loadData(); if (published) setTab('drafts'); }} />;
  }

  const statusChip = (c) => {
    const map = { draft: ['#6b7280', L('مسودة', 'Draft')], published: ['#22c55e', L('منشورة', 'Published')], failed: ['#ef4444', L('فشل النشر', 'Publish failed')] };
    const [color, label] = map[c.status] || map.draft;
    return <span style={{ fontSize: '0.68rem', fontWeight: 700, padding: '2px 10px', borderRadius: 20, background: color + '22', color }}>{label}</span>;
  };

  return (
    <div style={{ maxWidth: 960, margin: '0 auto' }}>
      {/* ── Hero ── */}
      <div className="cmpx-hero cmpx-fade">
        <div style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap' }}>
          <div style={{ minWidth: 220 }}>
            <div style={{ fontSize: '1.35rem', fontWeight: 800 }}>{L('الحملات الإعلانية', 'Ad campaigns')}</div>
            <div style={{ fontSize: '0.78rem', opacity: 0.92, marginTop: 5, lineHeight: 1.6 }}>
              {L('أنشئ حملة واحدة وانشرها على تيك توك وسناب شات وجوجل.', 'Build a campaign once — publish it to TikTok, Snapchat and Google.')}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
              <div className="cmpx-stat"><div style={{ fontSize: '0.62rem', opacity: 0.85 }}>{L('المسودات', 'Drafts')}</div><div style={{ fontWeight: 800, fontSize: '1.05rem' }}>{loading ? '…' : campaigns.length}</div></div>
              <div className="cmpx-stat"><div style={{ fontSize: '0.62rem', opacity: 0.85 }}>{L('منصات متصلة', 'Connected')}</div><div style={{ fontWeight: 800, fontSize: '1.05rem' }}>3</div></div>
              <div className="cmpx-stat" style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <span style={{ fontSize: '1rem' }}>🔒</span>
                <span style={{ fontSize: '0.66rem', lineHeight: 1.4, opacity: 0.95 }}>{L('كل حملة تُنشأ متوقفة —\nلا صرف تلقائي', 'Every campaign starts\npaused — no auto-spend')}</span>
              </div>
            </div>
          </div>
          {canEdit && <button className="cmpx-btn-hero" onClick={() => setWizard({ draft: null })}>+ {L('حملة جديدة', 'New campaign')}</button>}
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="cmpx-tabbar">
        {[['live', '📡', L('الحملات النشطة', 'Running')], ['drafts', '📝', L('المسودات', 'Drafts')], ['forms', '👤', L('نماذج العملاء', 'Lead forms')]].map(([id, icon, label]) => (
          <button key={id} className={`cmpx-tab${tab === id ? ' on' : ''}`} onClick={() => setTab(id)}>
            <span>{icon}</span>{label}
            {id === 'drafts' && campaigns.length > 0 && <span style={{ fontSize: '0.64rem', fontWeight: 800, padding: '1px 7px', borderRadius: 20, background: 'var(--accent)', color: '#fff' }}>{campaigns.length}</span>}
          </button>
        ))}
      </div>

      {msg && <div className="cmpx-fade" style={{ padding: 11, borderRadius: 10, marginBottom: 12, background: msg.type === 'error' ? '#ef444422' : '#22c55e22', color: msg.type === 'error' ? '#ef4444' : '#16a34a', fontSize: '0.85rem' }}>{msg.text}</div>}

      {tab === 'live' && <LiveCampaigns canPublish={canPublish} ar={ar} />}

      {tab === 'drafts' && (
        loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[0, 1, 2].map(i => <div key={i} className="cmp-skel" style={{ height: 84, borderRadius: 16 }} />)}
          </div>
        ) : error ? (
          <div className="cmpx-card cmpx-fade" style={{ padding: 30, textAlign: 'center' }}>
            <div style={{ fontSize: '1.6rem', marginBottom: 8 }}>😕</div>
            <p style={{ color: 'var(--error)', margin: '0 0 12px', fontSize: '0.86rem' }}>{L('تعذّر تحميل المسودات', 'Failed to load drafts')}</p>
            <button className="btn btn-secondary btn-sm" onClick={loadData}>{L('إعادة المحاولة', 'Retry')}</button>
          </div>
        ) : campaigns.length === 0 ? (
          <div className="cmpx-card cmpx-fade" style={{ padding: 44, textAlign: 'center', color: 'var(--text-secondary)' }}>
            <div style={{ fontSize: '2.2rem', marginBottom: 10 }}>📝</div>
            <div style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--text-primary)', marginBottom: 5 }}>{L('لا توجد مسودات بعد', 'No drafts yet')}</div>
            <div style={{ fontSize: '0.78rem', marginBottom: 16 }}>{L('الحملات التي تحفظها قبل النشر تظهر هنا.', 'Campaigns you save before publishing appear here.')}</div>
            {canEdit && <button className="cmpx-grad-btn" onClick={() => setWizard({ draft: null })}>{L('أنشئ أول حملة', 'Create your first campaign')}</button>}
          </div>
        ) : campaigns.map((c, i) => (
          <div key={c.id} className="cmpx-card cmpx-fade" style={{ padding: 16, marginBottom: 12, animationDelay: `${i * 45}ms` }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
              <div className="cmpx-row" style={{ minWidth: 0, flex: '1 1 260px' }}>
                <div style={{ width: 42, height: 42, borderRadius: 12, flexShrink: 0, background: 'var(--bg-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.15rem' }}>{(OBJ_META[c.objective] || ['📋'])[0]}</div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 700, fontSize: '0.94rem' }}>{c.name}</span>{statusChip(c)}
                  </div>
                  <div style={{ fontSize: '0.74rem', color: 'var(--text-secondary)', marginTop: 4, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span>{OBJ_LABEL[c.objective]?.[ar ? 1 : 0] || c.objective}</span>
                    <span>· {c.budget} {L('ر.س', 'SAR')}/{c.budgetType === 'lifetime' ? L('إجمالي', 'total') : L('يوم', 'day')}</span>
                    <span style={{ display: 'inline-flex', gap: 4 }}>
                      {c.platforms.map(p => (
                        <span key={p} title={p} style={{ width: 20, height: 20, borderRadius: 6, background: (PLATFORM_COLOR[p] || '#888') + '1f', color: PLATFORM_COLOR[p] || '#888', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.5rem', fontWeight: 800 }}>{PLATFORM_GLYPH[p] || '●'}</span>
                      ))}
                    </span>
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                {canEdit && <button className="btn btn-secondary btn-sm" onClick={() => setWizard({ draft: c })}>{L('تعديل', 'Edit')}</button>}
                {canEdit && <button className="btn btn-secondary btn-sm" onClick={() => handleDuplicate(c)} disabled={publishingId === c.id} title={L('إنشاء نسخة', 'Duplicate')}>⧉</button>}
                {canPublish && <button className="cmpx-grad-btn" style={{ padding: '7px 16px', fontSize: '0.78rem' }} onClick={() => handlePublish(c)} disabled={publishingId === c.id}>
                  {publishingId === c.id ? '…' : L('نشر', 'Publish')}</button>}
                {canEdit && <button className="btn btn-secondary btn-sm" onClick={() => handleDelete(c)} style={{ color: 'var(--error)' }}>×</button>}
              </div>
            </div>
            {c.publishResult && (
              <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 6 }}>
                {Object.entries(c.publishResult).map(([p, r]) => (
                  <div key={p} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: '0.75rem' }}>
                    <span style={{ flexShrink: 0 }}>{r.ok ? (r.partial ? '⚠️' : '✅') : '❌'}</span>
                    <span style={{ color: r.ok ? (r.partial ? '#b45309' : '#16a34a') : '#ef4444', minWidth: 0, lineHeight: 1.5 }}>
                      <strong>{(PLATFORMS.find(x => x.key === p)?.label || [p, p])[ar ? 1 : 0]}:</strong>{' '}
                      {r.ok ? (r.note || L('تم الإنشاء (متوقفة)', 'created (paused)')) : (r.message || r.error)}
                      {r.url && <> · <a href={r.url} target="_blank" rel="noreferrer">{L('فتح في المنصة', 'open in platform')}</a></>}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))
      )}

      {tab === 'forms' && (
        <>
          <SnapLeadRouting ar={ar} canPublish={canPublish} />
          <div className="cmpx-fade" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 14, padding: 13, fontSize: '0.76rem', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            💡 {L('نماذج تيك توك تُختار داخل معالج الحملة عند اختيار هدف "العملاء المحتملون". أول إعلان نموذج على تيك توك يتطلب توقيع اتفاقية Lead Ads مرة واحدة في مدير الإعلانات — إن فشل النشر عند مرحلة الإعلان فستظهر رسالة تيك توك حرفيًا.',
               'TikTok forms are picked inside the campaign wizard when the objective is "Leads". The first TikTok instant-form ad requires signing the Lead Ads agreement once in Ads Manager — if publishing fails at the ad stage, TikTok\'s exact message is shown.')}
          </div>
        </>
      )}
    </div>
  );
}

// ── Wizard: build a campaign in 4 small steps with a progress bar ──
function Wizard({ ar, canPublish, draft, onClose }) {
  const L = (a, e) => (ar ? a : e);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(() => (draft ? {
    name: draft.name, objective: draft.objective, platforms: draft.platforms.length ? draft.platforms : ['tiktok'],
    budget: draft.budget || 100, budgetType: draft.budgetType || 'daily',
    startDate: (draft.startDate || '').slice(0, 10), endDate: (draft.endDate || '').slice(0, 10),
    audience: { country: 'SA', ageMin: '18', ageMax: '45', gender: 'all', interests: [], cities: [], snapRegions: [], ...(draft.audience || {}) },
    creative: { headline: '', body: '', cta: 'LEARN_MORE', landingUrl: '', imageUrl: '', leadForms: {}, ...(draft.creative || {}) },
  } : emptyForm()));
  const [busy, setBusy] = useState(false);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [msg, setMsg] = useState(null);
  const [result, setResult] = useState(null); // publish outcome screen

  const u = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const uA = (k, v) => setForm(p => ({ ...p, audience: { ...p.audience, [k]: v } }));
  const uC = (k, v) => setForm(p => ({ ...p, creative: { ...p.creative, [k]: v } }));
  const togglePlatform = (k) => setForm(p => ({ ...p, platforms: p.platforms.includes(k) ? p.platforms.filter(x => x !== k) : [...p.platforms, k] }));

  const isLeads = form.objective === 'leads';
  const leadPlatforms = form.platforms.filter(p => LEAD_FORM_PLATFORMS.includes(p));
  const missingForms = isLeads ? leadPlatforms.filter(p => !(form.creative.leadForms || {})[p]) : [];
  const canNext = step !== 0 || (form.name.trim().length > 0 && form.platforms.length > 0);

  const onPickImage = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (!/^image\//.test(file.type)) { setMsg({ type: 'error', text: L('يُسمح بالصور فقط', 'Images only') }); return; }
    if (file.size > 10 * 1024 * 1024) { setMsg({ type: 'error', text: L('الحد الأقصى 10 ميجابايت', 'Max size is 10 MB') }); return; }
    setUploadingImg(true);
    const res = await uploadCampaignImage(file);
    setUploadingImg(false);
    if (res.error || !res.url) { setMsg({ type: 'error', text: res.error || L('فشل رفع الصورة', 'Image upload failed') }); return; }
    uC('imageUrl', res.url);
  };

  const persist = async () => {
    const res = await saveCampaign(draft?.id, form);
    if (res.error) { setMsg({ type: 'error', text: res.error }); return null; }
    return res.campaign;
  };
  const saveDraft = async () => { setBusy(true); const c = await persist(); setBusy(false); if (c) onClose(false); };
  const publishNow = async () => {
    setBusy(true); setMsg(null);
    const c = await persist();
    if (!c) { setBusy(false); return; }
    const res = await publishCampaign(c.id);
    setBusy(false);
    if (res.error) { setMsg({ type: 'error', text: res.error }); return; }
    setResult(res);
  };

  // ── Publish outcome screen ──
  if (result) {
    const allOk = Object.values(result.results || {}).every(r => r.ok && !r.partial);
    return (
      <div style={{ maxWidth: 620, margin: '0 auto' }} className="cmpx-fade">
        <div className="cmpx-card" style={{ padding: 28, textAlign: 'center', marginBottom: 14 }}>
          <div style={{ fontSize: '2.6rem', marginBottom: 8 }}>{result.status === 'published' ? (allOk ? '🎉' : '⚠️') : '❌'}</div>
          <div style={{ fontWeight: 800, fontSize: '1.1rem' }}>{result.status === 'published' ? L('تم إنشاء الحملة', 'Campaign created') : L('تعذّر النشر', 'Publishing failed')}</div>
          <div style={{ fontSize: '0.77rem', color: 'var(--text-secondary)', marginTop: 6, lineHeight: 1.6 }}>
            {L('الحملة متوقفة على المنصة — راجعها هناك ثم فعّلها بنفسك. لن يُصرف شيء قبل ذلك.', 'The campaign is PAUSED on the platform — review it there and switch it on yourself. Nothing spends before that.')}
          </div>
        </div>
        {Object.entries(result.results || {}).map(([p, r], i) => (
          <div key={p} className="cmpx-card cmpx-fade" style={{ padding: 15, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', animationDelay: `${i * 70}ms` }}>
            <div style={{ width: 38, height: 38, borderRadius: 10, flexShrink: 0, background: (PLATFORM_COLOR[p] || '#888') + '1f', color: PLATFORM_COLOR[p] || '#888', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '0.74rem' }}>{PLATFORM_GLYPH[p] || '●'}</div>
            <div style={{ flex: '1 1 220px', minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: '0.88rem' }}>{(PLATFORMS.find(x => x.key === p)?.label || [p, p])[ar ? 1 : 0]}</div>
              <div style={{ fontSize: '0.75rem', color: r.ok ? (r.partial ? '#b45309' : '#16a34a') : '#ef4444', marginTop: 2, lineHeight: 1.5 }}>
                {r.ok ? (r.partial ? '⚠️ ' : '✅ ') : '❌ '}{r.note || r.message || (r.ok ? L('تم الإنشاء (متوقفة)', 'Created (paused)') : r.error)}
              </div>
            </div>
            {r.url && <a className="btn btn-secondary btn-sm" href={r.url} target="_blank" rel="noreferrer" style={{ flexShrink: 0 }}>{L('فتح في المنصة', 'Open in platform')}</a>}
          </div>
        ))}
        <button className="cmpx-grad-btn" onClick={() => onClose(true)} style={{ width: '100%', padding: 13 }}>{L('تم', 'Done')}</button>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 760, margin: '0 auto' }} className="cmpx-fade">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <h2 style={{ margin: 0, fontSize: '1.2rem' }}>{draft?.id ? L('تعديل الحملة', 'Edit campaign') : L('حملة جديدة', 'New campaign')}</h2>
        <button className="btn btn-secondary btn-sm" onClick={() => onClose(false)}>{L('إلغاء', 'Cancel')}</button>
      </div>

      {/* Progress bar + step chips (done steps clickable) */}
      <div style={{ marginBottom: 18 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 7 }}>
          <span style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--accent)' }}>{STEPS[step][ar ? 0 : 1]}</span>
          <span style={{ fontSize: '0.68rem', color: 'var(--text-secondary)' }}>{L(`الخطوة ${step + 1} من 4`, `Step ${step + 1} of 4`)}</span>
        </div>
        <div className="cmpx-progress"><div style={{ width: `${((step + 1) / 4) * 100}%` }} /></div>
        <div style={{ display: 'flex', gap: 5, marginTop: 8, flexWrap: 'wrap' }}>
          {STEPS.map((s, i) => (
            <button key={i} type="button" onClick={() => i < step && setStep(i)}
              style={{ border: 'none', cursor: i < step ? 'pointer' : 'default', fontSize: '0.64rem', fontWeight: 600, padding: '3px 10px', borderRadius: 20,
                background: i === step ? 'var(--accent)' : i < step ? '#22c55e1c' : 'var(--bg-secondary)',
                color: i === step ? '#fff' : i < step ? '#16a34a' : 'var(--text-secondary)' }}>
              {i < step ? '✓ ' : ''}{s[ar ? 0 : 1]}
            </button>
          ))}
        </div>
      </div>

      {msg && <div style={{ padding: 10, borderRadius: 10, marginBottom: 12, background: '#ef444422', color: '#ef4444', fontSize: '0.85rem' }}>{msg.text}</div>}

      {/* ── Step 1: basics ── */}
      {step === 0 && (
        <div className="cmpx-card cmpx-fade" style={{ padding: 20 }}>
          <div className="input-group"><label>{L('اسم الحملة', 'Campaign name')}</label>
            <input autoFocus value={form.name} onChange={e => u('name', e.target.value)} placeholder={L('مثال: حملة يمام ١٥ — أغسطس', 'e.g. Yamam 15 — August push')} /></div>

          <div className="input-group"><label>{L('ماذا تريد من هذه الحملة؟', 'What should this campaign do?')}</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(165px, 1fr))', gap: 9 }}>
              {OBJECTIVES.map(o => (
                <button key={o} type="button" className={`cmpx-pick${form.objective === o ? ' on' : ''}`} onClick={() => u('objective', o)}>
                  <div style={{ fontSize: '1.15rem' }}>{OBJ_META[o][0]}</div>
                  <div style={{ fontWeight: 700, fontSize: '0.8rem', color: 'var(--text-primary)', marginTop: 4 }}>{OBJ_LABEL[o][ar ? 1 : 0]}</div>
                  <div style={{ fontSize: '0.66rem', color: 'var(--text-secondary)', marginTop: 2, lineHeight: 1.45 }}>{OBJ_META[o][1][ar ? 0 : 1]}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="input-group" style={{ marginBottom: 0 }}><label>{L('أين تُنشر؟', 'Where does it run?')}</label>
            <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap' }}>
              {PLATFORMS.map(p => {
                const on = form.platforms.includes(p.key);
                return (
                  <button key={p.key} type="button" disabled={!p.live} onClick={() => togglePlatform(p.key)}
                    className={`cmpx-pick${on ? ' on' : ''}`}
                    style={{ display: 'flex', alignItems: 'center', gap: 9, opacity: p.live ? 1 : 0.45,
                      ...(on ? { borderColor: PLATFORM_COLOR[p.key], boxShadow: `0 0 0 3px ${PLATFORM_COLOR[p.key]}22`, background: PLATFORM_COLOR[p.key] + '0d' } : {}) }}>
                    <span style={{ width: 26, height: 26, borderRadius: 7, background: PLATFORM_COLOR[p.key], color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.58rem', fontWeight: 800 }}>{PLATFORM_GLYPH[p.key]}</span>
                    <span style={{ fontSize: '0.82rem', fontWeight: on ? 800 : 500, color: 'var(--text-primary)' }}>{p.label[ar ? 1 : 0]}</span>
                    {!p.live && <span style={{ fontSize: '0.6rem', color: 'var(--text-secondary)' }}>({L('قريبًا', 'soon')})</span>}
                    {on && <span style={{ color: PLATFORM_COLOR[p.key], fontWeight: 800 }}>✓</span>}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ── Step 2: budget & audience ── */}
      {step === 1 && (
        <div className="cmpx-card cmpx-fade" style={{ padding: 20 }}>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <div className="input-group" style={{ flex: '1 1 150px' }}><label>{L('الميزانية', 'Budget')} ({L('ر.س', 'SAR')})</label>
              <input type="number" min="20" value={form.budget} onChange={e => u('budget', e.target.value)} />
              <div style={{ fontSize: '0.66rem', color: Number(form.budget) < 20 ? '#b45309' : 'var(--text-secondary)', marginTop: 3 }}>
                {L('الحد الأدنى للمنصات 20 ر.س', 'Platform minimum is 20 SAR')}</div></div>
            <div className="input-group" style={{ flex: '1 1 170px' }}><label>{L('نوع الميزانية', 'Budget type')}</label>
              <div className="cmpx-seg">
                <button type="button" className={form.budgetType === 'daily' ? 'on' : ''} onClick={() => u('budgetType', 'daily')}>{L('يومية', 'Daily')}</button>
                <button type="button" className={form.budgetType === 'lifetime' ? 'on' : ''} onClick={() => u('budgetType', 'lifetime')}>{L('إجمالية', 'Lifetime')}</button>
              </div></div>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <div className="input-group" style={{ flex: '1 1 150px' }}><label>{L('تبدأ في', 'Starts')}</label>
              <input type="date" value={form.startDate} onChange={e => u('startDate', e.target.value)} /></div>
            <div className="input-group" style={{ flex: '1 1 150px' }}><label>{L('تنتهي في', 'Ends')}</label>
              <input type="date" value={form.endDate} onChange={e => u('endDate', e.target.value)} /></div>
          </div>
          <div style={{ borderTop: '1px solid var(--border)', margin: '6px 0 14px' }} />
          <div style={{ fontWeight: 700, fontSize: '0.88rem', marginBottom: 10 }}>👥 {L('من يشاهد الإعلان؟', 'Who sees the ad?')}</div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <div className="input-group" style={{ flex: '1 1 110px' }}><label>{L('الدولة', 'Country')}</label>
              <input value={form.audience.country} onChange={e => uA('country', e.target.value)} placeholder="SA" /></div>
            <div className="input-group" style={{ flex: '1 1 90px' }}><label>{L('العمر من', 'Age from')}</label>
              <input type="number" value={form.audience.ageMin} onChange={e => uA('ageMin', e.target.value)} /></div>
            <div className="input-group" style={{ flex: '1 1 90px' }}><label>{L('إلى', 'to')}</label>
              <input type="number" value={form.audience.ageMax} onChange={e => uA('ageMax', e.target.value)} /></div>
            <div className="input-group" style={{ flex: '1 1 210px' }}><label>{L('الجنس', 'Gender')}</label>
              <div className="cmpx-seg">
                {[['all', L('الكل', 'All')], ['male', L('ذكور', 'Male')], ['female', L('إناث', 'Female')]].map(([v, lbl]) => (
                  <button key={v} type="button" className={form.audience.gender === v ? 'on' : ''} onClick={() => uA('gender', v)}>{lbl}</button>
                ))}
              </div></div>
          </div>
          {form.platforms.includes('tiktok') && (
            <TargetingPicker interests={form.audience.interests || []} cities={form.audience.cities || []}
              onInterests={(v) => uA('interests', v)} onCities={(v) => uA('cities', v)} ar={ar} />
          )}
          {form.platforms.includes('snapchat') && (
            <SnapGeoPicker country={form.audience.country} regions={form.audience.snapRegions || []}
              onRegions={(v) => uA('snapRegions', v)} ar={ar} />
          )}
        </div>
      )}

      {/* ── Step 3: creative (+ lead form when the objective is leads) ── */}
      {step === 2 && (
        <>
          {isLeads && (
            <div className="cmpx-card cmpx-fade" style={{ padding: 20, marginBottom: 12 }}>
              <div style={{ fontWeight: 700, fontSize: '0.92rem', marginBottom: 4 }}>👤 {L('نموذج بيانات العميل', 'Customer lead form')}</div>
              <div style={{ fontSize: '0.74rem', color: 'var(--text-secondary)', marginBottom: 12, lineHeight: 1.6 }}>
                {L('اختر النموذج الذي سيملؤه العميل (الاسم، الجوال…). يُنشأ النموذج نفسه في مدير إعلانات المنصة ثم تختاره هنا — بدون نموذج ستتحول الحملة لزيارات للرابط.',
                   'Pick the form the customer fills in (name, phone…). The form itself is built in the platform\'s Ads Manager — without one, the campaign falls back to traffic to your landing URL.')}
              </div>
              {leadPlatforms.map(p => (
                <LeadFormPicker key={p} platform={p} ar={ar}
                  value={(form.creative.leadForms || {})[p] || ''}
                  onChange={id => uC('leadForms', { ...(form.creative.leadForms || {}), [p]: id })} />
              ))}
              {leadPlatforms.length === 0 && (
                <div style={{ fontSize: '0.78rem', color: '#b45309' }}>
                  {L('نماذج البيانات متاحة على تيك توك وسناب شات فقط — اخترهما في الخطوة الأولى.', 'Lead forms are available on TikTok and Snapchat only — select them in step 1.')}
                </div>
              )}
              {leadPlatforms.includes('tiktok') && (
                <div style={{ marginTop: 8, fontSize: '0.68rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                  {L('ملاحظة تيك توك: أول إعلان نموذج يتطلب توقيع اتفاقية Lead Ads مرة واحدة في مدير الإعلانات — إن رفض النشر ستظهر رسالته حرفيًا.',
                     'TikTok note: the first instant-form ad requires the one-time Lead Ads agreement in Ads Manager — if publish is rejected, TikTok\'s exact message is shown.')}
                </div>
              )}
              {leadPlatforms.includes('tiktok') && (
                <div style={{ marginTop: 12, padding: 12, borderRadius: 10, background: 'var(--bg-primary)', border: '1px dashed var(--border)' }}>
                  <div style={{ fontWeight: 700, fontSize: '0.82rem', marginBottom: 6 }}>🧭 {L('لا يوجد نموذج بعد؟ أنشئه في تيك توك بهذه الأسئلة', 'No form yet? Create one in TikTok with these questions')}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 8 }}>
                    {L('استخدم هذه الأسئلة ليصل جواب كل عميل إلى الحقل الصحيح في النظام تلقائيًا:', 'Use these questions so each customer\'s answer lands in the right CRM field automatically:')}
                    <ul style={{ margin: '6px 0 0', paddingInlineStart: 18 }}>
                      <li>{L('الاسم — رقم الجوال (حقول تلقائية)', 'Name — Phone (prefill fields)')}</li>
                      <li>{L('ما ميزانيتك التقريبية؟', 'What\'s your approximate budget?')} <span style={{ color: 'var(--accent)', fontWeight: 700 }}>→ {L('حقل الميزانية', 'budget field')}</span></li>
                      <li>{L('ما هدفك من الشراء؟ (سكن / استثمار)', 'Purchase purpose? (living / investment)')}</li>
                      <li>{L('نوع الوحدة المطلوبة', 'Unit type wanted')}</li>
                      <li>{L('طريقة الشراء (كاش / تمويل بنكي)', 'Payment method (cash / bank finance)')}</li>
                      <li>{L('المشروع أو المدينة المهتم بها', 'Project or city of interest')}</li>
                    </ul>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <a href="https://ads.tiktok.com/i18n/material/instant-page" target="_blank" rel="noreferrer" style={{ padding: '7px 14px', borderRadius: 8, background: 'var(--accent)', color: '#fff', fontWeight: 700, fontSize: '0.78rem', textDecoration: 'none' }}>
                      {L('فتح منشئ النماذج في تيك توك ↗', 'Open TikTok form builder ↗')}
                    </a>
                    <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>{L('بعد إنشائه ونشره، اضغط «تحديث» بالأعلى لاختياره.', 'After you create & publish it, hit “refresh” above to pick it.')}</span>
                  </div>
                  <div style={{ fontSize: '0.66rem', color: 'var(--text-secondary)', marginTop: 6 }}>{L('يمكنك تعديل ربط الأسئلة بالحقول من الإعدادات ← ربط أسئلة النماذج.', 'You can tune the question→field mapping in Settings → Lead-form questions.')}</div>
                </div>
              )}
            </div>
          )}
          <div className="cmpx-card cmpx-fade" style={{ padding: 20 }}>
            <div style={{ fontWeight: 700, fontSize: '0.92rem', marginBottom: 10 }}>🖼 {L('محتوى الإعلان', 'Ad content')}</div>
            <div className="input-group"><label>{L('العنوان', 'Headline')}</label>
              <input value={form.creative.headline} onChange={e => uC('headline', e.target.value)} placeholder={L('عنوان قصير وجذاب', 'Short, catchy headline')} /></div>
            <div className="input-group"><label>{L('النص', 'Primary text')}</label>
              <textarea rows={2} value={form.creative.body} onChange={e => uC('body', e.target.value)} /></div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <div className="input-group" style={{ flex: '1 1 160px' }}><label>{L('زر الحث', 'Call to action')}</label>
                <select value={form.creative.cta} onChange={e => uC('cta', e.target.value)}>
                  {CTAS.map(c => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
                </select></div>
              <div className="input-group" style={{ flex: '2 1 220px' }}><label>{L('رابط الوجهة', 'Landing URL')}{isLeads && <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}> ({L('اختياري مع النموذج', 'optional with a form')})</span>}</label>
                <input value={form.creative.landingUrl} onChange={e => uC('landingUrl', e.target.value)} placeholder="https://" /></div>
            </div>
            <div className="input-group" style={{ marginBottom: 0 }}><label>{L('صورة الإعلان', 'Ad image')}</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                {form.creative.imageUrl
                  ? <img src={form.creative.imageUrl} alt="" style={{ width: 58, height: 58, objectFit: 'cover', borderRadius: 10, border: '1px solid var(--border)' }} />
                  : <div style={{ width: 58, height: 58, borderRadius: 10, border: '1.5px dashed var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)', fontSize: '1.3rem' }}>🖼</div>}
                <label className="btn btn-secondary btn-sm" style={{ cursor: uploadingImg ? 'wait' : 'pointer', margin: 0 }}>
                  {uploadingImg ? L('جارٍ الرفع…', 'Uploading…') : L('📤 رفع صورة', '📤 Upload image')}
                  <input type="file" accept="image/*" style={{ display: 'none' }} disabled={uploadingImg} onChange={onPickImage} />
                </label>
                {form.creative.imageUrl && <button type="button" className="btn btn-secondary btn-sm" onClick={() => uC('imageUrl', '')}>{L('إزالة', 'Remove')}</button>}
              </div>
              <div style={{ fontSize: '0.66rem', color: 'var(--text-secondary)', marginTop: 5 }}>
                {L('بدون صورة تُنشأ الحملة والمجموعة فقط، ويُكمَل الإعلان في المنصة.', 'Without an image, only the campaign + ad group are created; the ad is finished in the platform.')}</div>
            </div>
          </div>
        </>
      )}

      {/* ── Step 4: review ── */}
      {step === 3 && (
        <div className="cmpx-card cmpx-fade" style={{ padding: 20 }}>
          {[
            [L('الاسم', 'Name'), form.name || '—'],
            [L('الهدف', 'Objective'), `${OBJ_META[form.objective][0]} ${OBJ_LABEL[form.objective][ar ? 1 : 0]}`],
            [L('المنصات', 'Platforms'), form.platforms.map(p => (PLATFORMS.find(x => x.key === p)?.label || [p, p])[ar ? 1 : 0]).join('، ')],
            [L('الميزانية', 'Budget'), `${form.budget} ${L('ر.س', 'SAR')} / ${form.budgetType === 'lifetime' ? L('إجمالية', 'lifetime') : L('يومية', 'daily')}`],
            [L('المدة', 'Schedule'), (form.startDate || form.endDate) ? `${form.startDate || '—'} ← ${form.endDate || '—'}` : L('تبدأ غدًا لمدة شهر (افتراضي)', 'Starts tomorrow for a month (default)')],
            [L('الجمهور', 'Audience'), `${form.audience.country} · ${form.audience.ageMin}-${form.audience.ageMax} · ${form.audience.gender === 'male' ? L('ذكور', 'Male') : form.audience.gender === 'female' ? L('إناث', 'Female') : L('الكل', 'All')}`],
            [L('الإعلان', 'Creative'), [form.creative.headline, form.creative.cta.replace(/_/g, ' '), form.creative.landingUrl].filter(Boolean).join(' · ') || '—'],
          ].map(r => (
            <div key={r[0]} style={{ display: 'flex', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: '0.8rem' }}>
              <span style={{ color: 'var(--text-secondary)', minWidth: 92, flexShrink: 0 }}>{r[0]}</span>
              <span style={{ color: 'var(--text-primary)', minWidth: 0, wordBreak: 'break-word' }}>{r[1]}</span>
            </div>
          ))}
          {form.creative.imageUrl && (
            <div style={{ display: 'flex', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: '0.8rem', alignItems: 'center' }}>
              <span style={{ color: 'var(--text-secondary)', minWidth: 92, flexShrink: 0 }}>{L('الصورة', 'Image')}</span>
              <img src={form.creative.imageUrl} alt="" style={{ width: 46, height: 46, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--border)' }} />
            </div>
          )}
          {isLeads && (
            <div style={{ display: 'flex', gap: 10, padding: '8px 0', fontSize: '0.8rem' }}>
              <span style={{ color: 'var(--text-secondary)', minWidth: 92, flexShrink: 0 }}>{L('النموذج', 'Lead form')}</span>
              <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {leadPlatforms.map(p => (
                  <span key={p} style={{ fontSize: '0.7rem', fontWeight: 600, padding: '2px 10px', borderRadius: 20, background: (form.creative.leadForms || {})[p] ? '#22c55e22' : '#f59e0b22', color: (form.creative.leadForms || {})[p] ? '#16a34a' : '#b45309' }}>
                    {(PLATFORMS.find(x => x.key === p)?.label || [p, p])[ar ? 1 : 0]} {(form.creative.leadForms || {})[p] ? '✓' : L('بدون نموذج', 'no form')}
                  </span>
                ))}
              </span>
            </div>
          )}
          {missingForms.length > 0 && (
            <div style={{ marginTop: 10, padding: 11, borderRadius: 10, background: '#f59e0b14', border: '1px solid #f59e0b55', fontSize: '0.74rem', color: '#b45309', lineHeight: 1.5 }}>
              ⚠️ {L('لم تختر نموذجًا لبعض المنصات — ستُنشأ حملتها كزيارات للرابط بدلًا من جمع بيانات العملاء. ارجع لخطوة "الإعلان" لاختياره.',
                    'No form picked for some platforms — theirs will publish as traffic to the landing URL instead of collecting customer data. Go back to the Creative step to pick one.')}
            </div>
          )}
          <div style={{ marginTop: 12, padding: 11, borderRadius: 10, background: 'var(--bg-secondary)', fontSize: '0.72rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
            🔒 {L('تُنشأ الحملة متوقفة على كل المنصات — تراجعها وتفعّلها بنفسك هناك، فلا يُصرف شيء تلقائيًا.',
                  'The campaign is created PAUSED on every platform — you review and switch it on there yourself, so nothing spends automatically.')}
          </div>
        </div>
      )}

      {/* Footer */}
      <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
        {step > 0 && <button className="btn btn-secondary" onClick={() => setStep(step - 1)} disabled={busy}>{L('السابق', 'Back')}</button>}
        {step < 3 && <button className="cmpx-grad-btn" onClick={() => setStep(step + 1)} disabled={!canNext}
          title={!canNext ? L('أدخل اسم الحملة واختر منصة', 'Enter a name and pick a platform') : undefined}>{L('التالي', 'Next')} {ar ? '←' : '→'}</button>}
        {step === 3 && (
          <>
            <button className="btn btn-secondary" onClick={saveDraft} disabled={busy}>{busy ? '…' : L('حفظ كمسودة', 'Save as draft')}</button>
            {canPublish && <button className="cmpx-grad-btn" onClick={publishNow} disabled={busy}>{busy ? L('جارٍ النشر…', 'Publishing…') : `🚀 ${L('نشر الآن (متوقفة)', 'Publish now (paused)')}`}</button>}
          </>
        )}
      </div>
    </div>
  );
}

// Wire each Snapchat lead form to the CRM and say who its leads belong to. This used to
// be two env vars (secret + a formId:company string), so adding a form meant an SSH
// session and a redeploy — marketing couldn't connect a new form without a developer.
// Connecting registers a real webhook with Snapchat; the secret it returns is stored
// encrypted server-side and never shown here.
function SnapLeadRouting({ ar, canPublish }) {
  const L = (a, e) => (ar ? a : e);
  const [state, setState] = useState({ loading: true });
  const [brokers, setBrokers] = useState([]);
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    setState({ loading: true });
    const [res, b] = await Promise.all([fetchSnapLeadForms(), brokersList()]);
    setBrokers((b.brokers || []).map(x => x.name || x.company || '').filter(Boolean));
    setState({ loading: false, ...res });
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, f, extra) => {
    setBusy(f.id); setMsg(null);
    const res = await fn(f.id, { formName: f.name, brokerCompany: f.brokerCompany, integrationId: f.integrationId, ...extra });
    setBusy(null);
    if (res.error || res.ok === false) { setMsg({ type: 'error', text: res.message || res.error || L('فشل', 'Failed') }); return; }
    load();
  };

  const forms = state.forms || [];
  return (
    <div className="cmpx-card cmpx-fade" style={{ padding: 18, marginBottom: 16 }}>
      <div style={{ marginBottom: 4 }}><strong style={{ fontSize: '1rem' }}>{L('نماذج سناب ← العملاء المحتملون', 'Snap lead forms → CRM')}</strong></div>
      <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginBottom: 12 }}>
        {L('عند التوصيل، تصل بيانات العميل إلى النظام فور إرسال النموذج بدل انتظار ملف جوجل. حدد لمن تعود كل استمارة.',
           'Once connected, a submission reaches the CRM within seconds instead of waiting for the Google Sheet. Set who each form\'s leads belong to.')}
      </div>
      {msg && <div style={{ padding: 9, borderRadius: 8, marginBottom: 10, fontSize: '0.78rem', background: '#ef444422', color: '#ef4444' }}>{msg.text}</div>}
      {state.loading ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-secondary)', fontSize: '0.78rem' }}><span className="cmp-spin" />{L('جارٍ التحميل…', 'Loading…')}</div>
      ) : !state.ok ? (
        <div style={{ fontSize: '0.78rem', color: 'var(--error)' }}>
          {state.message || state.error || L('تعذّر التحميل', 'Could not load')}
          {' '}<button type="button" className="btn btn-secondary btn-sm" onClick={load}>{L('إعادة المحاولة', 'Retry')}</button>
        </div>
      ) : forms.length === 0 ? (
        <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>{L('لا توجد نماذج على هذا الحساب', 'No lead forms on this ad account')}</div>
      ) : forms.map(f => (
        <div key={f.id} style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 11, marginBottom: 8, background: 'var(--bg-card)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ flexShrink: 0, fontSize: '0.62rem', fontWeight: 700, padding: '2px 9px', borderRadius: 20, background: (f.connected ? '#22c55e' : '#6b7280') + '22', color: f.connected ? '#16a34a' : '#6b7280' }}>
              {f.connected ? L('موصول', 'Connected') : L('غير موصول', 'Not connected')}
            </span>
            <span style={{ fontWeight: 600, fontSize: '0.85rem', flex: '1 1 160px', minWidth: 0 }}>{f.name}</span>
            {canPublish && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <select value={f.brokerCompany || ''} disabled={busy === f.id}
                  onChange={e => act(setSnapLeadFormOwner, f, { brokerCompany: e.target.value })} style={{ padding: '5px 7px', fontSize: '0.78rem' }}>
                  <option value="">{L('عملاء يمام', 'Yamam leads')}</option>
                  {brokers.map(b => <option key={b} value={b}>{b}</option>)}
                </select>
                <button className="btn btn-sm" disabled={busy === f.id}
                  onClick={() => act(f.connected ? disconnectSnapLeadForm : connectSnapLeadForm, f)}
                  style={{ background: f.connected ? '#ef4444' : '#22c55e', color: '#fff', minWidth: 74 }}>
                  {busy === f.id ? '…' : (f.connected ? L('فصل', 'Disconnect') : L('توصيل', 'Connect'))}
                </button>
              </div>
            )}
          </div>
          {(f.fields || []).length > 0 && (
            <div style={{ marginTop: 7, display: 'flex', gap: 5, flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ fontSize: '0.68rem', color: 'var(--text-secondary)' }}>{L('يُطلب:', 'Asks for:')}</span>
              {f.fields.map((x, i) => (
                <span key={i} style={{ fontSize: '0.67rem', padding: '1px 7px', borderRadius: 20, background: 'var(--bg-secondary)', border: '1px solid var(--border)' }}>
                  {LEAD_FIELD_LABEL[x]?.[ar ? 0 : 1] || String(x).replace(/_/g, ' ')}
                </span>
              ))}
            </div>
          )}
          {/* Registered elsewhere — Snapchat allows only one webhook per form, so this
              explains why Connect would fail rather than leaving a silent error. */}
          {f.otherUrl && (
            <div style={{ marginTop: 6, fontSize: '0.68rem', color: '#b45309' }}>
              {L('مسجّل على وجهة أخرى:', 'Already registered to another destination:')} {f.otherUrl}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// One platform's lead-form dropdown. The forms themselves live on the platform (built in
// its Ads Manager); we list them so a draft can point at one, and show which fields the
// chosen form asks the customer for so that's visible before publishing.
// Audience targeting (TikTok): searchable interest + city multi-selects. Empty = whole country.
function TargetingPicker({ interests, cities, onInterests, onCities, ar }) {
  const L = (a, e) => (ar ? a : e);
  const [data, setData] = useState(null);
  const [qi, setQi] = useState('');
  const [qc, setQc] = useState('');
  useEffect(() => { fetchTargeting('tiktok').then(setData); }, []);
  const allI = (data && data.interests) || [];
  const allR = (data && data.regions) || [];
  const nameI = Object.fromEntries(allI.map((x) => [x.id, x.name]));
  const nameR = Object.fromEntries(allR.map((x) => [x.id, x.name]));
  const matchI = qi.trim() ? allI.filter((x) => (x.name || '').toLowerCase().includes(qi.toLowerCase()) && !interests.includes(x.id)).slice(0, 20) : [];
  const matchR = qc.trim() ? allR.filter((x) => (x.name || '').toLowerCase().includes(qc.toLowerCase()) && !cities.includes(x.id)).slice(0, 20) : [];
  const inp = { width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.84rem' };
  const chip = (id, label, remove) => (
    <span key={id} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 8px', borderRadius: 14, background: 'var(--accent)', color: '#fff', fontSize: '0.74rem', fontWeight: 700 }}>
      {label}<span onClick={remove} style={{ cursor: 'pointer', opacity: 0.85 }}>✕</span>
    </span>
  );
  const menu = (items, pick) => items.length > 0 && (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, marginTop: 4, maxHeight: 180, overflowY: 'auto', background: 'var(--bg-secondary)' }}>
      {items.map((x) => <div key={x.id} onClick={() => pick(x.id)} style={{ padding: '7px 10px', cursor: 'pointer', fontSize: '0.82rem' }}>{x.name}</div>)}
    </div>
  );
  return (
    <div style={{ marginTop: 14, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
      <div style={{ fontWeight: 700, fontSize: '0.84rem', marginBottom: 8 }}>🎯 {L('اهتمامات الجمهور ومدنه — تيك توك (اختياري)', 'Audience interests & cities — TikTok (optional)')}</div>
      <div className="input-group"><label>{L('الاهتمامات', 'Interests')}</label>
        {interests.length > 0 && <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>{interests.map((id) => chip(id, nameI[id] || id, () => onInterests(interests.filter((x) => x !== id))))}</div>}
        <input style={inp} value={qi} onChange={(e) => setQi(e.target.value)} placeholder={L('ابحث: عقارات، تمويل، سيارات…', 'Search: real estate, finance, autos…')} />
        {menu(matchI, (id) => { onInterests([...interests, id]); setQi(''); })}
        {data && data.interestsError && <div style={{ fontSize: '0.7rem', color: '#b45309', marginTop: 4 }}>{data.interestsError}</div>}
      </div>
      <div className="input-group" style={{ marginTop: 8 }}><label>{L('المدن/المناطق', 'Cities / regions')}</label>
        {cities.length > 0 && <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>{cities.map((id) => chip(id, nameR[id] || id, () => onCities(cities.filter((x) => x !== id))))}</div>}
        <input style={inp} value={qc} onChange={(e) => setQc(e.target.value)} placeholder={L('ابحث: الرياض، جدة، الدمام…', 'Search: Riyadh, Jeddah, Dammam…')} />
        {menu(matchR, (id) => { onCities([...cities, id]); setQc(''); })}
        {data && data.regionsError && <div style={{ fontSize: '0.7rem', color: '#b45309', marginTop: 4 }}>{data.regionsError}</div>}
      </div>
      <div style={{ fontSize: '0.68rem', color: 'var(--text-secondary)', marginTop: 6 }}>{L('اتركها فارغة لاستهداف الدولة كاملة.', 'Leave empty to target the whole country.')}</div>
    </div>
  );
}

// Snapchat geo picker — regions only (Snap's own list, keyed by Snap region IDs, which are
// NOT interchangeable with TikTok's). Empty = the whole country.
function SnapGeoPicker({ country, regions, onRegions, ar }) {
  const L = (a, e) => (ar ? a : e);
  const [data, setData] = useState(null);
  const [q, setQ] = useState('');
  useEffect(() => { fetchTargeting('snapchat', country).then(setData); }, [country]);
  const all = (data && data.regions) || [];
  const nameR = Object.fromEntries(all.map((x) => [x.id, x.name]));
  const match = q.trim() ? all.filter((x) => (x.name || '').toLowerCase().includes(q.toLowerCase()) && !regions.includes(x.id)).slice(0, 20) : [];
  const inp = { width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.84rem' };
  return (
    <div style={{ marginTop: 14, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
      <div style={{ fontWeight: 700, fontSize: '0.84rem', marginBottom: 8 }}>🎯 {L('مناطق الجمهور — سناب شات (اختياري)', 'Audience regions — Snapchat (optional)')}</div>
      <div className="input-group"><label>{L('المناطق', 'Regions')}</label>
        {regions.length > 0 && <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>{regions.map((id) => (
          <span key={id} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 8px', borderRadius: 14, background: 'var(--accent)', color: '#fff', fontSize: '0.74rem', fontWeight: 700 }}>
            {nameR[id] || id}<span onClick={() => onRegions(regions.filter((x) => x !== id))} style={{ cursor: 'pointer', opacity: 0.85 }}>✕</span>
          </span>
        ))}</div>}
        <input style={inp} value={q} onChange={(e) => setQ(e.target.value)} placeholder={L('ابحث: الرياض، مكة، الشرقية…', 'Search: Riyadh, Makkah, Eastern…')} />
        {match.length > 0 && (
          <div style={{ border: '1px solid var(--border)', borderRadius: 8, marginTop: 4, maxHeight: 180, overflowY: 'auto', background: 'var(--bg-secondary)' }}>
            {match.map((x) => <div key={x.id} onClick={() => { onRegions([...regions, x.id]); setQ(''); }} style={{ padding: '7px 10px', cursor: 'pointer', fontSize: '0.82rem' }}>{x.name}</div>)}
          </div>
        )}
        {data && data.regionsError && <div style={{ fontSize: '0.7rem', color: '#b45309', marginTop: 4 }}>{data.regionsError}</div>}
      </div>
      <div style={{ fontSize: '0.68rem', color: 'var(--text-secondary)', marginTop: 6 }}>{L('اتركها فارغة لاستهداف الدولة كاملة.', 'Leave empty to target the whole country.')}</div>
    </div>
  );
}

function LeadFormPicker({ platform, value, onChange, ar }) {
  const L = (a, e) => (ar ? a : e);
  const [state, setState] = useState({ loading: true });

  const load = useCallback(async () => {
    setState({ loading: true });
    const res = await fetchLeadForms(platform);
    setState({ loading: false, ...res });
  }, [platform]);
  useEffect(() => { load(); }, [load]);

  const forms = state.forms || [];
  const selected = forms.find(f => f.id === value);

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <span style={{ width: 24, height: 24, borderRadius: 6, background: PLATFORM_COLOR[platform], color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.62rem', fontWeight: 800 }}>{PLATFORM_GLYPH[platform]}</span>
        <strong style={{ fontSize: '0.85rem' }}>{platform === 'tiktok' ? L('تيك توك', 'TikTok') : L('سناب شات', 'Snapchat')}</strong>
      </div>
      {state.loading ? (
        <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>{L('جارٍ تحميل النماذج…', 'Loading forms…')}</div>
      ) : !state.ok ? (
        <div>
          <div style={{ fontSize: '0.78rem', color: 'var(--error)' }}>
            {state.message || state.error || L('تعذّر تحميل النماذج', 'Could not load forms')}
            {' '}<button type="button" className="btn btn-secondary btn-sm" onClick={load}>{L('إعادة المحاولة', 'Retry')}</button>
          </div>
          {/* The list needs a permission the ad token may not have, but publishing with a
              form only needs its id — so it stays usable by pasting the id from Ads Manager. */}
          {state.allowManual && (
            <div style={{ marginTop: 6 }}>
              <input value={value} onChange={e => onChange(e.target.value.trim())}
                placeholder={L('الصق معرّف النموذج (Form ID) من مدير الإعلانات', 'Paste the form ID from Ads Manager')} />
            </div>
          )}
        </div>
      ) : forms.length === 0 ? (
        <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
          {L('لا توجد نماذج على هذا الحساب — أنشئ واحدًا في مدير الإعلانات ثم أعد التحميل.', 'No forms on this account — create one in Ads Manager, then reload.')}
          {state.url && <> <a href={state.url} target="_blank" rel="noreferrer">{L('فتح مدير الإعلانات', 'Open Ads Manager')}</a></>}
          {' '}<button type="button" className="btn btn-secondary btn-sm" onClick={load}>{L('تحديث', 'Reload')}</button>
        </div>
      ) : (
        <>
          <select value={value} onChange={e => onChange(e.target.value)}>
            <option value="">{L('— بدون نموذج (زيارات للرابط) —', '— none (traffic to landing URL) —')}</option>
            {forms.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
          {selected && (selected.fields || []).length > 0 && (
            <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>{L('يُطلب من العميل:', 'Asks the customer for:')}</span>
              {selected.fields.map((f, i) => (
                <span key={i} style={{ fontSize: '0.7rem', fontWeight: 600, padding: '2px 8px', borderRadius: 20, background: 'var(--bg-secondary)', border: '1px solid var(--border)' }}>
                  {LEAD_FIELD_LABEL[f]?.[ar ? 0 : 1] || f.replace(/_/g, ' ')}
                </span>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Real delivery state (not just the on/off toggle) → chip color + label. TikTok returns
// `deliveryStatus`; platforms that don't (Snap/Google) fall back to ENABLE/DISABLE so a
// campaign that's "on" but not actually delivering is no longer mislabelled "Active".
const DELIVERY_META = {
  delivering:  ['#22c55e', '#16a34a', ['نشطة', 'Active']],
  paused:      ['#9ca3af', '#6b7280', ['متوقفة', 'Paused']],
  review:      ['#f59e0b', '#b45309', ['قيد المراجعة', 'In review']],
  rejected:    ['#ef4444', '#dc2626', ['مرفوضة', 'Rejected']],
  ended:       ['#94a3b8', '#64748b', ['انتهت مدتها', 'Ended']],
  budget:      ['#f59e0b', '#b45309', ['بلغت الميزانية', 'Budget capped']],
  scheduled:   ['#3b82f6', '#2563eb', ['مجدولة', 'Scheduled']],
  no_delivery: ['#f59e0b', '#b45309', ['لا تُعرض', 'Not delivering']],
  no_ads:      ['#9ca3af', '#6b7280', ['بدون إعلانات', 'No ads']],
  frozen:      ['#ef4444', '#dc2626', ['مجمّدة', 'Frozen']],
  deleted:     ['#9ca3af', '#6b7280', ['محذوفة', 'Deleted']],
};
function DeliveryChip({ ds, status, ar, size }) {
  const [dot, text, label] = DELIVERY_META[ds] || (status === 'ENABLE' ? DELIVERY_META.delivering : DELIVERY_META.paused);
  return (
    <span style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: size === 'sm' ? '0.6rem' : '0.66rem', fontWeight: 700, padding: '2px 9px', borderRadius: 20, background: dot + '22', color: text }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: dot }} />{label[ar ? 0 : 1]}
    </span>
  );
}

const fmtNum = (n) => (n == null ? '—' : n >= 1e6 ? (n / 1e6).toFixed(1) + 'M' : n >= 1e3 ? (n / 1e3).toFixed(1) + 'K' : Math.round(n).toLocaleString());
const fmtMoney = (n) => (n == null ? '—' : Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
// Lifetime spend/impressions/clicks/conversions strip, shown next to a live campaign/ad
// group/ad. Renders nothing when there are no synced metrics yet (m == null).
function MetricsStrip({ m, ar, size }) {
  if (!m) return null;
  const L = (a, e) => (ar ? a : e);
  const items = [
    [L('إنفاق', 'Spend'), fmtMoney(m.spend) + ' ' + L('ر.س', 'SAR'), 'var(--accent)'],
    [L('ظهور', 'Impr'), fmtNum(m.impressions), 'var(--text-primary)'],
    [L('نقرات', 'Clicks'), fmtNum(m.clicks), 'var(--text-primary)'],
    [L('تحويلات', 'Conv'), fmtNum(m.conversions), 'var(--success)'],
  ];
  return (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: size === 'sm' ? '0.64rem' : '0.72rem', marginTop: 4 }}>
      {items.map(([lbl, val, col], i) => (
        <span key={i} style={{ color: 'var(--text-secondary)' }}>{lbl}: <strong style={{ color: col, fontWeight: 700 }}>{val}</strong></span>
      ))}
    </div>
  );
}

// Running campaigns on each connected platform — normalized status (Active/Paused),
// with pause/resume + budget edit. This is the "platform aggregator" view: one UI,
// each platform's live campaigns fetched through its adapter.
function LiveCampaigns({ canPublish, ar }) {
  const L = (a, e) => (ar ? a : e);
  const [platform, setPlatform] = useState('tiktok');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [editBudget, setEditBudget] = useState({});
  const [expanded, setExpanded] = useState(null);
  const [details, setDetails] = useState({});
  const [openGroups, setOpenGroups] = useState({});
  const [selectedAd, setSelectedAd] = useState(null);
  const [adEdit, setAdEdit] = useState({ text: '' });
  const [adBusy, setAdBusy] = useState(false);
  const [adBlobUrl, setAdBlobUrl] = useState(null);

  // A Snap video's proxy URL (/api/...) needs the JWT header, so a raw <video src> would
  // 401. Fetch it as an authed blob when the ad modal opens; a public URL (TikTok) plays
  // as-is. Revoke the object URL on close to avoid leaking blobs.
  useEffect(() => {
    setAdBlobUrl(null);
    // Seed the edit field from THIS ad (blank for Snap, which has no text on the ad object)
    // and reset on close — otherwise the box shows the previous ad's text and a stray
    // "Save text" would push ad A's copy onto ad B.
    setAdEdit({ text: selectedAd?.adText || '' });
    const v = selectedAd?.videoUrl;
    if (!v || !v.startsWith('/api/')) return;
    let url = null, cancelled = false;
    campaignVideoBlobUrl(v).then(u => { if (cancelled) { URL.revokeObjectURL(u); } else { url = u; setAdBlobUrl(u); } }).catch(() => {});
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [selectedAd]);

  // Ad-level edits. Only what each platform accepts as an incremental change: TikTok
  // patches ad_text, Snapchat patches the creative's headline. Anything outside those
  // allowlists would need a full-object rewrite and could null out a live ad.
  const saveAdText = async () => {
    setAdBusy(true);
    const fields = platform === 'snapchat'
      ? { creativeId: selectedAd.creativeId, headline: adEdit.text }
      : { text: adEdit.text, adgroupId: selectedAd.adgroupId };
    const res = await updateLiveAd(selectedAd.id, fields, platform);
    setAdBusy(false);
    if (res.error || res.ok === false) { window.alert(res.message || res.error || L('فشل', 'Failed')); return; }
    if (res.note) window.alert(res.note);
    setSelectedAd(null);
    if (expanded) reloadDetail(expanded);
  };
  const toggleAd = async () => {
    setAdBusy(true);
    const res = await setLiveAdStatus(selectedAd.id, selectedAd.status !== 'ENABLE', platform, selectedAd.adgroupId);
    setAdBusy(false);
    if (res.error || res.ok === false) { window.alert(res.message || res.error || L('فشل', 'Failed')); return; }
    setSelectedAd(null);
    if (expanded) reloadDetail(expanded);
  };
  const [imgAccess, setImgAccess] = useState(null); // null | 'checking' | {granted}
  const [agBusyId, setAgBusyId] = useState(null);
  const [agBudget, setAgBudget] = useState({});
  const toggleGroup = (gid) => setOpenGroups(o => ({ ...o, [gid]: !o[gid] }));

  // Re-read one campaign's ad groups after an edit, so the row shows what the platform
  // actually stored rather than what we optimistically hoped.
  const reloadDetail = async (campaignId) => {
    setDetails(d => ({ ...d, [campaignId]: { loading: true } }));
    const res = await fetchLiveCampaignDetail(campaignId, platform);
    setDetails(d => ({ ...d, [campaignId]: { loading: false, data: res } }));
  };
  const toggleAdgroup = async (campaignId, g) => {
    setAgBusyId(g.id);
    const res = await setLiveAdgroupStatus(g.id, g.status !== 'ENABLE', platform, campaignId);
    setAgBusyId(null);
    if (res.error || res.ok === false) { window.alert(res.message || res.error || L('فشل', 'Failed')); return; }
    reloadDetail(campaignId);
  };
  const saveAdgroupBudget = async (campaignId, g) => {
    const v = agBudget[g.id];
    setAgBudget(p => { const n = { ...p }; delete n[g.id]; return n; });
    if (!v || Number(v) <= 0) return;
    setAgBusyId(g.id);
    const res = await updateLiveAdgroup(g.id, Number(v), platform, campaignId);
    setAgBusyId(null);
    if (res.error || res.ok === false) { window.alert(res.message || res.error || L('فشل', 'Failed')); return; }
    if (res.appliesNextDay) window.alert(L('تم الحفظ. تغييرات الميزانية اليومية على تيك توك تسري من الساعة 12:00 صباحًا في اليوم التالي.', 'Saved. TikTok applies daily-budget changes from 00:00 the following day.'));
    reloadDetail(campaignId);
  };
  const checkAccess = async () => {
    setImgAccess('checking');
    setImgAccess(await checkTiktokCreativeAccess());
  };
  const AdMedia = (a, size) => (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0, borderRadius: 8, overflow: 'hidden', background: a.thumbnail ? 'var(--bg-primary)' : 'linear-gradient(135deg, var(--accent), #7c3aed)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {a.thumbnail
        ? <img src={a.thumbnail} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        : <span style={{ fontSize: size / 2.6, color: '#fff', opacity: 0.92 }}>{a.mediaType === 'video' ? '▶' : '🖼'}</span>}
    </div>
  );
  const TypeBadge = (a) => (
    <>
      {a.mediaType && <span style={{ fontSize: '0.56rem', fontWeight: 700, padding: '1px 6px', borderRadius: 5, background: 'var(--bg-secondary)', color: 'var(--text-secondary)', border: '1px solid var(--border)' }}>{a.mediaType === 'video' ? L('فيديو', 'Video') : L('صورة', 'Image')}</span>}
      {a.smartPlus && <span style={{ fontSize: '0.56rem', fontWeight: 700, padding: '1px 6px', borderRadius: 5, background: '#4285f422', color: '#4285f4' }}>Smart+</span>}
    </>
  );
  const genderText = (gd) => (gd === 'male' ? L('ذكور', 'Male') : gd === 'female' ? L('إناث', 'Female') : L('الجميع', 'All'));
  const TargetChip = (icon, label, value) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '0.66rem', padding: '3px 9px', borderRadius: 20, background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
      <span>{icon}</span><span style={{ color: 'var(--text-secondary)' }}>{label}:</span><strong style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{value}</strong>
    </span>
  );
  const TargetingRow = (g) => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '10px 12px', borderTop: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
      {TargetChip('👥', L('الجنس', 'Gender'), genderText(g.gender))}
      {TargetChip('🎂', L('العمر', 'Age'), (g.ages && g.ages.length) ? g.ages.join('، ') : L('كل الأعمار', 'All ages'))}
      {g.locations && g.locations.length ? TargetChip('📍', L('الموقع', 'Location'), g.locations.slice(0, 4).join('، ') + (g.locations.length > 4 ? ` +${g.locations.length - 4}` : '')) : null}
      {g.optimizationGoal ? TargetChip('🎯', L('الهدف', 'Goal'), String(g.optimizationGoal).replace(/_/g, ' ')) : null}
    </div>
  );
  const AdCard = (a) => (
    <button type="button" key={a.id} onClick={() => setSelectedAd(a)} className="cmp-thumb" title={a.name}
      style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'start', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 10, background: 'var(--bg-card)', cursor: 'pointer' }}>
      {AdMedia(a, 46)}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '0.76rem', fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.name}</div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 3 }}>{TypeBadge(a)}</div>
        {a.metrics && <MetricsStrip m={a.metrics} ar={ar} size="sm" />}
      </div>
      <DeliveryChip ds={a.deliveryStatus} status={a.status} ar={ar} size="sm" />
    </button>
  );

  const openDetail = async (c) => {
    if (expanded === c.id) { setExpanded(null); return; }
    setExpanded(c.id);
    if (!details[c.id]) {
      setDetails(d => ({ ...d, [c.id]: { loading: true } }));
      const res = await fetchLiveCampaignDetail(c.id, platform);
      setDetails(d => ({ ...d, [c.id]: { loading: false, data: res } }));
    }
  };

  const load = useCallback(async (p) => {
    setLoading(true);
    const res = await fetchLiveCampaigns(p);
    setData(res); setLoading(false);
  }, []);
  // Switching platform tab: drop the previous platform's per-campaign UI state so a stale
  // expanded row / cached detail / open edit box can't bleed across platforms.
  useEffect(() => {
    setExpanded(null); setDetails({}); setOpenGroups({}); setSelectedAd(null);
    setEditBudget({}); setAgBudget({});
    load(platform);
  }, [platform, load]);

  const toggle = async (c) => {
    setBusyId(c.id);
    const res = await setLiveCampaignStatus(c.id, c.status !== 'ENABLE', platform);
    setBusyId(null);
    if (res.error || res.ok === false) { window.alert(res.message || res.error || L('فشل', 'Failed')); return; }
    load(platform);
  };
  const saveBudget = async (c) => {
    const v = editBudget[c.id];
    if (!v || Number(v) <= 0) { setEditBudget(p => { const n = { ...p }; delete n[c.id]; return n; }); return; }
    setBusyId(c.id);
    const res = await updateLiveCampaign(c.id, { budget: Number(v) }, platform);
    setBusyId(null);
    setEditBudget(p => { const n = { ...p }; delete n[c.id]; return n; });
    if (res.error || res.ok === false) { window.alert(res.message || res.error || L('فشل', 'Failed')); return; }
    load(platform);
  };

  return (
    <div className="cmpx-card cmpx-fade" style={{ padding: 18, marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <strong style={{ fontSize: '1rem' }}>{L('الحملات النشطة', 'Running campaigns')}</strong>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginTop: 2 }}>{L('إدارة حملاتك مباشرة عبر منصات الإعلان · الأرقام إجمالية طوال عمر الحملة', 'Manage your live campaigns across ad platforms · figures are lifetime totals')}</div>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          {LIVE_PLATFORMS.map(p => {
            const on = platform === p.key;
            return (
              <button key={p.key} onClick={() => setPlatform(p.key)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '6px 13px', borderRadius: 10, cursor: 'pointer', transition: 'all .15s ease',
                  border: `1.5px solid ${on ? PLATFORM_COLOR[p.key] : 'var(--border)'}`,
                  background: on ? PLATFORM_COLOR[p.key] + '14' : 'transparent',
                  color: on ? 'var(--text-primary)' : 'var(--text-secondary)', fontSize: '0.76rem', fontWeight: on ? 800 : 600,
                  boxShadow: on ? `0 0 0 3px ${PLATFORM_COLOR[p.key]}1c` : 'none' }}>
                <span style={{ width: 18, height: 18, borderRadius: 5, background: PLATFORM_COLOR[p.key] || '#888', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.46rem', fontWeight: 800 }}>{PLATFORM_GLYPH[p.key]}</span>
                {p.label[ar ? 1 : 0]}
              </button>
            );
          })}
          <button onClick={() => load(platform)} className="btn btn-secondary btn-sm" title={L('تحديث', 'Refresh')}>↻</button>
          {platform === 'tiktok' && (
            <button onClick={checkAccess} className="btn btn-secondary btn-sm" disabled={imgAccess === 'checking'} title={L('التحقق من صلاحية عرض صور الإعلانات', 'Check TikTok ad-image access')}>
              {imgAccess === 'checking' ? '…' : L('فحص صور الإعلانات', 'Check image access')}
            </button>
          )}
          {AUTH_PATHS[platform] && (
            <a href={AUTH_PATHS[platform]} className="btn btn-secondary btn-sm" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 5 }}
              title={L('إعادة ربط الحساب مع المنصة', 'Reconnect this platform account')}>
              🔗 {L('إعادة الربط', 'Reconnect')}
            </a>
          )}
        </div>
      </div>
      {platform === 'tiktok' && imgAccess && imgAccess !== 'checking' && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '8px 11px', marginBottom: 12, borderRadius: 8, fontSize: '0.72rem', lineHeight: 1.5,
          background: imgAccess.granted ? '#22c55e14' : '#f59e0b14', border: `1px solid ${imgAccess.granted ? '#22c55e55' : '#f59e0b55'}`, color: 'var(--text-secondary)' }}>
          <span style={{ flexShrink: 0 }}>{imgAccess.images && imgAccess.videos ? '✅' : imgAccess.granted ? '◐' : '⚠️'}</span>
          {/* Images and videos are separate TikTok permissions — one is commonly granted
              without the other, so they're reported separately rather than as one flag. */}
          <span>
            {L('الفيديو:', 'Video:')} {imgAccess.videos ? L('مسموح ✅', 'allowed ✅') : L('غير مسموح', 'not allowed')}
            {' · '}{L('الصور:', 'Images:')} {imgAccess.images ? L('مسموح ✅', 'allowed ✅') : L('غير مسموح', 'not allowed')}
            {imgAccess.images
              // Smart+ builds its own creatives, which never enter the asset library, so
              // TikTok cannot hand back a still for them at any permission level.
              ? <>{' — '}{L('إعلانات Smart+ تُنشئ تصاميمها تلقائيًا ولا تتوفر لها صورة ثابتة عبر الـAPI؛ تُعرض صورة الفيديو بدلًا منها.',
                            'Smart+ ads generate their own creatives, which have no still available via the API — the video poster is shown instead.')}</>
              : <>{' — '}{L('لتفعيل الصور فعّل "Image Management → Read Image Library" على تطبيق تيك توك.',
                            'To enable images, turn on "Creative Management → Image Management → Read Image Library" on your TikTok app.')}
                  {' '}<a href="/auth/tiktok" style={{ color: 'var(--accent)', fontWeight: 700, whiteSpace: 'nowrap' }}>{L('إعادة الربط ←', 'Reconnect →')}</a></>}
          </span>
        </div>
      )}
      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[0, 1, 2].map(i => <div key={i} className="cmp-skel" style={{ height: 64, borderRadius: 12 }} />)}
        </div>
      ) : !data || !data.ok ? (
        (data?.error === 'not_connected' || data?.error === 'missing_credentials') ? (
          <div style={{ padding: 22, textAlign: 'center', border: '1px dashed var(--border)', borderRadius: 12, color: 'var(--text-secondary)', fontSize: '0.82rem' }}>
            <div style={{ fontSize: '1.7rem', marginBottom: 6, opacity: 0.55 }}>🔌</div>
            {data?.message || L('هذه المنصة غير متصلة بعد', 'This platform isn’t connected yet')}
          </div>
        ) : (
          <div style={{ padding: 22, textAlign: 'center', border: '1px dashed var(--border)', borderRadius: 12, color: 'var(--text-secondary)', fontSize: '0.82rem' }}>
            <div style={{ fontSize: '1.7rem', marginBottom: 6, opacity: 0.55 }}>⚠️</div>
            <div style={{ marginBottom: 10 }}>{data?.message || data?.error || L('تعذّر تحميل الحملات', 'Could not load campaigns')}</div>
            <button className="btn btn-secondary btn-sm" onClick={() => load(platform)}>{L('إعادة المحاولة', 'Retry')}</button>
          </div>
        )
      ) : data.campaigns.length === 0 ? (
        <div style={{ padding: 22, textAlign: 'center', border: '1px dashed var(--border)', borderRadius: 12, color: 'var(--text-secondary)', fontSize: '0.82rem' }}>
          <div style={{ fontSize: '1.7rem', marginBottom: 6, opacity: 0.55 }}>📭</div>
          {L('لا توجد حملات على هذه المنصة', 'No campaigns on this platform yet')}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {data.campaigns.map(c => {
            const active = c.status === 'ENABLE';
            const det = details[c.id];
            const isOpen = expanded === c.id;
            const color = PLATFORM_COLOR[platform] || '#888';
            return (
              <div key={c.id} style={{ border: `1.5px solid ${isOpen ? 'var(--accent)' : 'var(--border)'}`, borderRadius: 16, background: 'var(--bg-card)', overflow: 'hidden', transition: 'border-color .15s ease, box-shadow .2s ease', boxShadow: isOpen ? '0 8px 24px rgba(0,0,0,.10)' : 'none' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', flexWrap: 'wrap' }}>
                  <div onClick={() => openDetail(c)} style={{ display: 'flex', alignItems: 'center', gap: 12, flex: '1 1 220px', minWidth: 0, cursor: 'pointer' }}>
                    <div style={{ width: 38, height: 38, borderRadius: 10, flexShrink: 0, background: color + '1f', color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '0.8rem' }}>{PLATFORM_GLYPH[platform] || '●'}</div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                        <span style={{ fontWeight: 700, fontSize: '0.9rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.name}</span>
                        <DeliveryChip ds={c.deliveryStatus} status={c.status} ar={ar} />
                      </div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginTop: 3, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                        <span>{OBJ_LABEL[c.objective]?.[ar ? 1 : 0] || c.objective}</span>
                        {c.budget ? <span>{L('الميزانية', 'Budget')}: {c.budget} {L('ر.س', 'SAR')}</span> : null}
                        {c.deliveryStatus === 'delivering' && Number.isFinite(c.adgroupsTotal) && c.adgroupsTotal > 0 && (
                          <span title={L('مجموعات إعلانية تُعرض حاليًا', 'Ad groups currently delivering')}>{L(`${c.adgroupsLive} من ${c.adgroupsTotal} مجموعة تُعرض`, `${c.adgroupsLive}/${c.adgroupsTotal} ad groups live`)}</span>
                        )}
                        <span style={{ color: 'var(--accent)', fontWeight: 600 }}>{isOpen ? `▾ ${L('إخفاء التفاصيل', 'Hide details')}` : `▸ ${L('عرض التفاصيل', 'View details')}`}</span>
                      </div>
                      <MetricsStrip m={c.metrics} ar={ar} />
                    </div>
                  </div>
                  {canPublish && (
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
                      {editBudget[c.id] !== undefined ? (
                        <>
                          <input type="number" value={editBudget[c.id]} onChange={e => setEditBudget(p => ({ ...p, [c.id]: e.target.value }))} style={{ width: 84, padding: '5px 7px' }} />
                          <button className="btn btn-primary btn-sm" onClick={() => saveBudget(c)} disabled={busyId === c.id}>✓</button>
                          <button className="btn btn-secondary btn-sm" onClick={() => setEditBudget(p => { const n = { ...p }; delete n[c.id]; return n; })}>×</button>
                        </>
                      ) : (
                        <>
                          {/* Only campaigns that carry their own budget can be edited here.
                              Ours put the budget on the ad group — edit it in the details. */}
                          {c.budget ? <button className="btn btn-secondary btn-sm" onClick={() => setEditBudget(p => ({ ...p, [c.id]: String(c.budget) }))}>{L('الميزانية', 'Budget')}</button> : null}
                          <button className="btn btn-sm" onClick={() => toggle(c)} disabled={busyId === c.id}
                            style={{ background: active ? '#ef4444' : '#22c55e', color: '#fff', minWidth: 66 }}>{busyId === c.id ? '…' : (active ? L('إيقاف', 'Pause') : L('تشغيل', 'Resume'))}</button>
                        </>
                      )}
                    </div>
                  )}
                </div>
                {isOpen && (
                  <div style={{ borderTop: '1px solid var(--border)', background: 'var(--bg-secondary)', padding: 14 }}>
                    {!det || det.loading ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-secondary)', fontSize: '0.78rem' }}><span className="cmp-spin" />{L('جارٍ تحميل المجموعات والإعلانات…', 'Loading ad groups & ads…')}</div>
                    ) : !det.data || !det.data.ok ? (
                      <span style={{ color: 'var(--text-secondary)', fontSize: '0.78rem' }}>{det.data?.message || det.data?.error || L('لا توجد تفاصيل', 'No details available')}</span>
                    ) : (() => {
                      const groups = det.data.adgroups || [];
                      const ads = det.data.ads || [];
                      const known = new Set(groups.map(g => String(g.id)));
                      const orphan = ads.filter(a => !known.has(String(a.adgroupId)));
                      const renderGroup = (g, gAds, key, label) => {
                        const go = !!openGroups[key];
                        return (
                          <div key={key} style={{ border: '1px solid var(--border)', borderRadius: 10, marginBottom: 8, overflow: 'hidden', background: 'var(--bg-card)' }}>
                            <div onClick={() => toggleGroup(key)} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '9px 11px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                                <span style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>{go ? '▾' : '▸'}</span>
                                <span style={{ fontWeight: 600, fontSize: '0.82rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
                                {g && <DeliveryChip ds={g.deliveryStatus} status={g.status} ar={ar} size="sm" />}
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                                <span style={{ fontSize: '0.68rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{gAds.length} {L('إعلان', 'ads')}{g && g.budget ? ` · ${g.budget} ${L('ر.س', 'SAR')}` : ''}</span>
                                {/* The budget lives here, on the ad group — not on the campaign. */}
                                {g && canPublish && (agBudget[g.id] !== undefined ? (
                                  <>
                                    <input type="number" value={agBudget[g.id]} onChange={e => setAgBudget(p => ({ ...p, [g.id]: e.target.value }))} style={{ width: 78, padding: '4px 6px' }} />
                                    <button className="btn btn-primary btn-sm" onClick={() => saveAdgroupBudget(c.id, g)} disabled={agBusyId === g.id}>✓</button>
                                    <button className="btn btn-secondary btn-sm" onClick={() => setAgBudget(p => { const n = { ...p }; delete n[g.id]; return n; })}>×</button>
                                  </>
                                ) : (
                                  <>
                                    <button className="btn btn-secondary btn-sm" onClick={() => setAgBudget(p => ({ ...p, [g.id]: String(g.budget || '') }))}>{L('الميزانية', 'Budget')}</button>
                                    <button className="btn btn-sm" onClick={() => toggleAdgroup(c.id, g)} disabled={agBusyId === g.id}
                                      style={{ background: g.status === 'ENABLE' ? '#ef4444' : '#22c55e', color: '#fff', minWidth: 60 }}>{agBusyId === g.id ? '…' : (g.status === 'ENABLE' ? L('إيقاف', 'Pause') : L('تشغيل', 'Resume'))}</button>
                                  </>
                                ))}
                              </div>
                            </div>
                            {g && g.metrics && <div style={{ padding: '0 11px 9px' }}><MetricsStrip m={g.metrics} ar={ar} size="sm" /></div>}
                            {go && (
                              <>
                                {g && (g.gender || (g.ages && g.ages.length) || (g.locations && g.locations.length) || g.optimizationGoal) && TargetingRow(g)}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: 12, borderTop: '1px solid var(--border)' }}>
                                  {gAds.length ? gAds.map(AdCard) : <span style={{ color: 'var(--text-secondary)', fontSize: '0.72rem' }}>{L('لا توجد إعلانات في هذه المجموعة', 'No ads in this group')}</span>}
                                </div>
                              </>
                            )}
                          </div>
                        );
                      };
                      return (
                        <>
                          <div style={{ display: 'flex', gap: 14, marginBottom: 10, fontSize: '0.72rem', color: 'var(--text-secondary)' }}>
                            <span><strong style={{ color: 'var(--text-primary)' }}>{groups.length}</strong> {L('مجموعة إعلانية', 'ad groups')}</span>
                            <span><strong style={{ color: 'var(--text-primary)' }}>{ads.length}</strong> {L('إعلان', 'ads')}</span>
                          </div>
                          {det.data.previewDenied && (
                            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '8px 11px', marginBottom: 10, borderRadius: 8, background: '#f59e0b14', border: '1px solid #f59e0b55', fontSize: '0.7rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                              <span style={{ flexShrink: 0 }}>ℹ️</span>
                              <span>
                                {L('معاينات الإعلانات (الصور/الفيديو) غير متاحة لأن رمز تيك توك الحالي لا يملك صلاحية الوصول لمكتبة الوسائط. جميع الأسماء والحالات والميزانيات دقيقة ومباشرة من تيك توك.', 'Ad creative previews are unavailable because the current TikTok token isn’t granted media-library access. All names, statuses and budgets are accurate and live from TikTok.')}
                                {' '}<a href="/auth/tiktok" style={{ color: 'var(--accent)', fontWeight: 700, whiteSpace: 'nowrap' }}>{L('إعادة ربط تيك توك ←', 'Reconnect TikTok →')}</a>
                              </span>
                            </div>
                          )}
                          {groups.map(g => renderGroup(g, ads.filter(a => String(a.adgroupId) === String(g.id)), g.id, g.name))}
                          {orphan.length > 0 && renderGroup(null, orphan, '__orphan_' + c.id, L('إعلانات أخرى', 'Other ads'))}
                          {groups.length === 0 && orphan.length === 0 && <span style={{ color: 'var(--text-secondary)', fontSize: '0.78rem' }}>{L('لا توجد مجموعات إعلانية بعد', 'No ad groups yet')}</span>}
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {data && data.ok && data.url && <div style={{ marginTop: 12, textAlign: 'center' }}><a href={data.url} target="_blank" rel="noreferrer" style={{ fontSize: '0.75rem', color: 'var(--accent)', fontWeight: 600 }}>{L('فتح مدير الإعلانات', 'Open Ads Manager')} ↗</a></div>}

      {selectedAd && (
        <div onClick={() => setSelectedAd(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'var(--bg-card)', borderRadius: 14, width: '100%', maxWidth: 460, maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,.4)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, background: 'var(--bg-card)' }}>
              <strong style={{ fontSize: '0.9rem' }}>{L('تفاصيل الإعلان', 'Ad details')}</strong>
              <button onClick={() => setSelectedAd(null)} className="btn btn-secondary btn-sm">✕</button>
            </div>
            <div style={{ width: '100%', background: selectedAd.thumbnail ? 'var(--bg-primary)' : 'linear-gradient(135deg, var(--accent), #7c3aed)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, minHeight: 200, maxHeight: 360, overflow: 'hidden', color: '#fff' }}>
              {(() => {
                const needsProxy = selectedAd.videoUrl && selectedAd.videoUrl.startsWith('/api/');
                const playSrc = needsProxy ? adBlobUrl : selectedAd.videoUrl;
                return playSrc
                  ? <video src={playSrc} poster={selectedAd.thumbnail || undefined} controls autoPlay playsInline style={{ width: '100%', maxHeight: 360, background: '#000' }} />
                  : needsProxy
                  ? (selectedAd.thumbnail
                      ? <img src={selectedAd.thumbnail} alt="" style={{ width: '100%', maxHeight: 360, objectFit: 'contain', opacity: 0.6 }} />
                      : <span style={{ fontSize: '0.74rem', fontWeight: 600, opacity: 0.95 }}>{L('جارٍ تحميل الفيديو…', 'Loading video…')}</span>)
                  : null;
              })() || (selectedAd.thumbnail
                ? <img src={selectedAd.thumbnail} alt="" style={{ width: '100%', maxHeight: 360, objectFit: 'contain' }} />
                : (
                  <>
                    <span style={{ fontSize: '2.6rem' }}>{selectedAd.mediaType === 'video' ? '▶' : '🖼'}</span>
                    <span style={{ fontSize: '0.74rem', fontWeight: 600, opacity: 0.95 }}>{selectedAd.mediaType === 'video' ? L('إعلان فيديو', 'Video creative') : L('إعلان صورة', 'Image creative')}</span>
                    <span style={{ fontSize: '0.62rem', opacity: 0.8 }}>{L('المعاينة غير متاحة لهذا الرمز', 'Preview not available for this token')}</span>
                  </>
                ))}
            </div>
            <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 700, fontSize: '0.92rem' }}>{selectedAd.name}</span>
                <DeliveryChip ds={selectedAd.deliveryStatus} status={selectedAd.status} ar={ar} />
                {TypeBadge(selectedAd)}
              </div>
              {selectedAd.adText && <div style={{ fontSize: '0.8rem', color: 'var(--text-primary)', lineHeight: 1.5 }}>{selectedAd.adText}</div>}
              {selectedAd.metrics && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '10px 12px', borderRadius: 10, background: 'var(--bg-secondary)' }}>
                  {[[L('الإنفاق', 'Spend'), fmtMoney(selectedAd.metrics.spend) + ' ' + L('ر.س', 'SAR'), 'var(--accent)'],
                    [L('الظهور', 'Impressions'), fmtNum(selectedAd.metrics.impressions), 'var(--text-primary)'],
                    [L('النقرات', 'Clicks'), fmtNum(selectedAd.metrics.clicks), 'var(--text-primary)'],
                    [L('التحويلات', 'Conversions'), fmtNum(selectedAd.metrics.conversions), 'var(--success)']].map(([lbl, val, col]) => (
                    <div key={lbl} style={{ flex: '1 1 88px', textAlign: 'center' }}>
                      <div style={{ fontWeight: 800, fontSize: '0.95rem', color: col }}>{val}</div>
                      <div style={{ fontSize: '0.64rem', color: 'var(--text-secondary)', marginTop: 2 }}>{lbl}</div>
                    </div>
                  ))}
                </div>
              )}
              {[
                [L('الحث على الإجراء', 'Call to action'), selectedAd.cta && selectedAd.cta.replace(/_/g, ' ')],
                [L('معرّف الإعلان', 'Ad ID'), selectedAd.id],
              ].filter(r => r[1]).map(r => (
                <div key={r[0]} style={{ display: 'flex', gap: 8, fontSize: '0.76rem' }}>
                  <span style={{ color: 'var(--text-secondary)', minWidth: 110, flexShrink: 0 }}>{r[0]}</span>
                  <span style={{ color: 'var(--text-primary)', wordBreak: 'break-word' }}>{r[1]}</span>
                </div>
              ))}
              {selectedAd.landingUrl && (
                <div style={{ display: 'flex', gap: 8, fontSize: '0.76rem' }}>
                  <span style={{ color: 'var(--text-secondary)', minWidth: 110, flexShrink: 0 }}>{L('رابط الوجهة', 'Landing URL')}</span>
                  <a href={selectedAd.landingUrl} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)', wordBreak: 'break-all' }}>{selectedAd.landingUrl}</a>
                </div>
              )}
              {canPublish && (
                <div style={{ borderTop: '1px solid var(--border)', paddingTop: 10, marginTop: 4, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <input value={adEdit.text} onChange={e => setAdEdit(p => ({ ...p, text: e.target.value }))}
                    placeholder={platform === 'snapchat' ? L('العنوان (حتى 34 حرفًا)', 'Headline (max 34 chars)') : L('نص الإعلان', 'Ad text')} />
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <button className="btn btn-primary btn-sm" disabled={adBusy || !adEdit.text.trim()} onClick={saveAdText}>
                      {adBusy ? '…' : L('حفظ النص', 'Save text')}</button>
                    <button className="btn btn-sm" disabled={adBusy} onClick={toggleAd}
                      style={{ background: selectedAd.status === 'ENABLE' ? '#ef4444' : '#22c55e', color: '#fff' }}>
                      {adBusy ? '…' : (selectedAd.status === 'ENABLE' ? L('إيقاف الإعلان', 'Pause ad') : L('تشغيل الإعلان', 'Resume ad'))}</button>
                  </div>
                  {/* Snapchat re-reviews every ad sharing the creative, and its docs say
                      those ads are paused until re-approved — worth saying before the click. */}
                  <div style={{ fontSize: '0.68rem', color: 'var(--text-secondary)' }}>
                    {platform === 'snapchat'
                      ? L('تعديل العنوان يُعيد مراجعة كل إعلان يستخدم نفس التصميم في سناب.', 'Editing the headline re-reviews every ad using this creative on Snapchat.')
                      : L('يُعدَّل الاسم والنص فقط؛ الصورة والرابط والنموذج تُغيَّر في مدير إعلانات تيك توك.', 'Name and text only; image, URL and form are changed in TikTok Ads Manager.')}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CampaignComposer;
